/**
 * The one owner of posthog-js loading and initialization, shared by
 * `PostHogProvider.tsx` (mount-time init) and `funnel.ts` (event capture).
 *
 * Before this module existed, each of those files lazily imported posthog-js
 * on its own and only the Provider called `init()` — so a funnel event fired
 * before the Provider's effect finished (the earliest `trip_created` /
 * `itinerary_viewed` on a fresh page load) saw `__loaded === false` and was
 * dropped permanently. `ensurePostHog()` closes that race by initializing on
 * first demand, whichever caller gets there first; init still runs exactly
 * once (single module-level promise, plus an `__loaded` guard for hot-reload
 * style double evaluation).
 *
 * Gating is unchanged: production + `NEXT_PUBLIC_POSTHOG_KEY` only, and the
 * import stays dynamic so a dev or no-key environment never pulls posthog-js
 * into the bundle at all.
 */

import { buildPostHogInitConfig } from './posthog-init-config';

export const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

type PostHogClient = typeof import('posthog-js').default;

function posthogKey(): string | undefined {
  // An empty or whitespace-only value (an unset Vercel field saved as "")
  // counts as missing, same as the host below.
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY?.trim();
  return key ? key : undefined;
}

/**
 * `?? default` alone treats `NEXT_PUBLIC_POSTHOG_HOST=""` as a configured
 * host and hands posthog-js an empty api_host; empty/whitespace now falls
 * back to the US cloud default like a genuinely unset var.
 */
export function resolvePostHogHost(): string {
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST?.trim();
  return host ? host : DEFAULT_POSTHOG_HOST;
}

function isAnalyticsEnabled(): boolean {
  return process.env.NODE_ENV === 'production' && Boolean(posthogKey());
}

let clientPromise: Promise<PostHogClient> | null = null;

/**
 * Resolves to an initialized PostHog client, or `null` when analytics is off
 * (SSR, non-production, or no key). Never rejects on env gating — a failed
 * dynamic import still rejects, and callers swallow that.
 */
export function ensurePostHog(): Promise<PostHogClient | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const key = posthogKey();
  if (!isAnalyticsEnabled() || !key) return Promise.resolve(null);
  clientPromise ??= import('posthog-js').then((module) => {
    const posthog = module.default;
    if (!posthog.__loaded) {
      posthog.init(key, buildPostHogInitConfig());
    }
    return posthog;
  });
  return clientPromise;
}
